import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const AttendanceHistory = () => {
  const [history, setHistory] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [stats, setStats] = useState({ total: 0, present: 0, absent: 0, late: 0 });
  const [className, setClassName] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar'); // 'calendar' ou 'list'
  const [filters, setFilters] = useState({
    status: '',
    teacher_id: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.teacher_id) params.append('teacher_id', filters.teacher_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const res = await api.get(`/teacher/teacher-attendance/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
      setTeachers(res.data.teachers || []);
      setStats(res.data.stats || { total: 0, present: 0, absent: 0, late: 0 });
      setClassName(res.data.class_name || '');
    } catch (err) {
      console.error('Erreur historique:', err);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchHistory();
  };

  const resetFilters = () => {
    setFilters({ status: '', teacher_id: '', date_from: '', date_to: '' });
    setTimeout(fetchHistory, 100);
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'present': return '🟢';
      case 'absent': return '🔴';
      case 'late': return '🟠';
      case 'cancelled': return '⚪';
      default: return '';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'present': return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">🟢 Présent</span>;
      case 'absent': return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">🔴 Absent</span>;
      case 'late': return <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">🟠 Retard</span>;
      default: return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">-</span>;
    }
  };

  // ═══ Préparation de la vue calendrier ═══
  // 1. Toutes les dates uniques, triées
  const allDates = Array.from(new Set(history.map(h => h.date_raw))).sort();
  
  // 2. Pour chaque prof, pour chaque date, on cherche le statut
  const teacherList = Array.from(
    new Map(history.map(h => [h.teacher_id, h.teacher_name])).entries()
  ).map(([id, name]) => ({ id, name }));

  // Filtre sur les profs si le filtre teacher_id est actif
  const displayedTeachers = filters.teacher_id
    ? teacherList.filter(t => String(t.id) === String(filters.teacher_id))
    : teacherList;

  const getStatusForCell = (teacherId, dateRaw) => {
    const record = history.find(
      h => String(h.teacher_id) === String(teacherId) && h.date_raw === dateRaw
    );
    return record ? record.status : null;
  };

  return (
    <div className="p-6 lg:p-8 bg-white min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#0A0A0A]">📜 Historique des présences</h1>
        <p className="text-gray-500 mt-1">
          Classe {className} — Toutes les présences enregistrées
        </p>
      </div>

      {/* ═══ STATS ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <p className="text-xs text-gray-500 mb-1">📊 Total</p>
          <p className="text-2xl font-bold text-[#0A0A0A]">{stats.total}</p>
        </div>
        <div className="bg-white rounded-2xl border border-green-100 p-5">
          <p className="text-xs text-gray-500 mb-1">🟢 Présents</p>
          <p className="text-2xl font-bold text-green-600">{stats.present}</p>
        </div>
        <div className="bg-white rounded-2xl border border-red-100 p-5">
          <p className="text-xs text-gray-500 mb-1">🔴 Absents</p>
          <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
        </div>
        <div className="bg-white rounded-2xl border border-orange-100 p-5">
          <p className="text-xs text-gray-500 mb-1">🟠 Retards</p>
          <p className="text-2xl font-bold text-orange-600">{stats.late}</p>
        </div>
      </div>

      {/* ═══ FILTRES ═══ */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12]"
          >
            <option value="">Tous les statuts</option>
            <option value="present">🟢 Présent</option>
            <option value="absent">🔴 Absent</option>
            <option value="late">🟠 Retard</option>
          </select>

          <select
            value={filters.teacher_id}
            onChange={(e) => setFilters({ ...filters, teacher_id: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12]"
          >
            <option value="">Tous les professeurs</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>{t.name} ({t.subject_name})</option>
            ))}
          </select>

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12]"
            title="Du"
          />

          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#F39C12]"
            title="Au"
          />

          <div className="flex gap-2">
            <button
              onClick={handleFilter}
              className="flex-1 bg-[#F39C12] text-white px-4 py-2 rounded-lg hover:bg-[#E67E22] transition font-medium"
            >
              🔍 Filtrer
            </button>
            <button
              onClick={resetFilters}
              className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition"
              title="Réinitialiser"
            >
              ↺
            </button>
          </div>
        </div>
      </div>

      {/* ═══ TOGGLE VUE ═══ */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setView('calendar')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            view === 'calendar'
              ? 'bg-[#F39C12] text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📅 Vue calendrier
        </button>
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            view === 'list'
              ? 'bg-[#F39C12] text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          📋 Vue liste
        </button>
      </div>

      {/* ═══ CONTENU ═══ */}
      {loading ? (
        <div className="text-center py-20 text-gray-500">Chargement...</div>
      ) : history.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <p className="text-5xl mb-3">📭</p>
          <p className="text-lg font-bold text-[#0A0A0A] mb-2">Aucune présence enregistrée</p>
          <p className="text-sm text-gray-500">Les présences apparaîtront ici.</p>
        </div>
      ) : view === 'calendar' ? (
        /* ═══ VUE CALENDRIER ═══ */
        <div className="bg-white rounded-2xl border border-gray-100 overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 bg-gray-50 text-left py-3 px-4 text-xs font-semibold text-gray-600 min-w-[180px] z-10">
                  Professeur
                </th>
                {allDates.map((d) => {
                  const [y, m, day] = d.split('-');
                  return (
                    <th key={d} className="text-center py-3 px-2 text-xs font-semibold text-gray-600 min-w-[70px]">
                      {day}/{m}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {displayedTeachers.map((t) => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="sticky left-0 bg-white py-3 px-4 text-sm font-medium text-[#0A0A0A] border-r border-gray-100">
                    {t.name}
                  </td>
                  {allDates.map((d) => {
                    const status = getStatusForCell(t.id, d);
                    return (
                      <td key={d} className="text-center py-3 px-2 text-lg">
                        {status ? (
                          <span title={status}>{getStatusDot(status)}</span>
                        ) : (
                          <span className="text-gray-200">·</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* ═══ VUE LISTE ═══ */
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Professeur</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Matière</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Statut</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Notes</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Signalé</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm font-medium">{h.date}</td>
                  <td className="py-3 px-4 text-sm">{h.teacher_name}</td>
                  <td className="py-3 px-4 text-sm">{h.subject_name}</td>
                  <td className="py-3 px-4">{getStatusBadge(h.status)}</td>
                  <td className="py-3 px-4 text-xs text-gray-500 max-w-xs truncate">{h.notes || '-'}</td>
                  <td className="py-3 px-4 text-xs">
                    {h.is_reported_to_admin ? (
                      <span className="text-orange-600 font-medium">✅ Signalé</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ LÉGENDE ═══ */}
      <div className="mt-6 bg-blue-50 rounded-2xl p-4 flex items-center gap-6 flex-wrap">
        <span className="text-sm font-medium text-gray-700">Légende :</span>
        <span className="text-sm">🟢 Présent</span>
        <span className="text-sm">🔴 Absent</span>
        <span className="text-sm">🟠 Retard</span>
        <span className="text-sm text-gray-400">· Aucune donnée</span>
      </div>
    </div>
  );
};

export default AttendanceHistory;